import { useSearchParams } from "react-router-dom";
import { Alert } from "@hagerty/react-components";
import {
  Customer,
  formatPhoneNumber,
  getDisplayName,
  phoneNumbers,
} from "shared";
import {
  useContactId,
  useIsEmailVerified,
  useEnvironment,
  useIsSso,
  useIsStateFarmPartitioned,
  useUrls,
} from "../../app/Providers/AppContext";
import { useDialog } from "../Dialog";
import { mt } from "../../utils/tracking";
import { Link } from "../Link";
import { SettingsSection } from "./SettingsSection/SettingsSection";
import { RockAnim } from "../Other/RockAnim";
import { useScrollToTop } from "../../hooks/useScrollToTop";
import { formatCustomerAddress } from "./utils/formatCustomerAddress";
import { PreferencesInfoDialog } from "./PreferencesInfoDialog";
import { SettingsContainer } from "../SettingsContainer/SettingsContainer";
import { routes } from "../../routing/routes";
import { usePolicySummaries } from "../../hooks/usePolicySummaries";
import { StackableButtonGroup } from "./StackableButtonGroup";
import { StackableButton } from "./StackableButton";
import { useFeatureFlag } from "../../app/Providers/AppContext";

import "./Settings.scss";

type SourceType = "cutomer name" | "phone number" | "mailing address";

export type SettingsProps = {
  initialCustomer: Customer;
};

/**
 * The account settings page
 */
export const Settings = ({ initialCustomer }: SettingsProps) => {
  const [searchParams] = useSearchParams();

  useScrollToTop();

  const successParam = searchParams.get("success");
  const policies = usePolicySummaries(true);
  // If policy status is unknown, play it safe by assuming policy holder
  const isPolicyHolder = policies ? policies.length > 0 : true;
  const hasApexPolicy = !!policies && policies.some((p) => p.source === "APEX");
  const hasCorePolicy = !!policies && policies.some((p) => p.source === "CORE");
  const isUSPolicyHolder =
    !!policies && policies.some((p) => p.country === "USA");

  return (
    <>
      <RockAnim squashId="account-settings" />
      <SettingsWrapped
        initialCustomer={initialCustomer}
        isPolicyHolder={isPolicyHolder}
        hasApexPolicy={hasApexPolicy}
        hasCorePolicy={hasCorePolicy}
        isUSPolicyHolder={isUSPolicyHolder}
        didUpdateSucceed={successParam === "true"}
      />
    </>
  );
};

type SettingsWrappedProps = SettingsProps & {
  isPolicyHolder: boolean;
  hasApexPolicy: boolean;
  hasCorePolicy: boolean;
  isUSPolicyHolder: boolean;
  didUpdateSucceed?: boolean;
};

const SettingsWrapped = ({
  initialCustomer,
  isPolicyHolder,
  hasApexPolicy,
  hasCorePolicy,
  isUSPolicyHolder,
  didUpdateSucceed,
}: SettingsWrappedProps) => {
  const isStateFarm = useIsStateFarmPartitioned();
  const isStateFarmSsoLogin = isStateFarm && useIsSso();
  const urls = useUrls().account;
  const contactId = useContactId();
  const preferenceInfoDialog = useDialog();
  const isProd = useEnvironment() === "production";
  const isEmailVerified = useIsEmailVerified();
  const isCustomerNameEditable = !isPolicyHolder && !isStateFarm;
  const { SETTINGS_MFA_FOR_ALL, PAPERLESS } = useFeatureFlag();

  return (
    <SettingsContainer>
      <h2 className="mb-4">Your account settings</h2>
      {hasApexPolicy && (
        <Alert
          alertType="warning"
          hasIcon
          messageText={
            <>
              You are participating in the Hagerty Enthusiast+ program. For
              policy related changes, please call{" "}
              <strong>
                <Link phoneNumber={phoneNumbers.apex} />
              </strong>
              .
            </>
          }
        />
      )}
      {!isStateFarmSsoLogin && (
        <SettingsSection title="Login">
          <StackableButtonGroup>
            <StackableButton
              label={isEmailVerified ? "Verified email" : "Unverified email"}
              labelStyle={isEmailVerified ? "success" : "error"}
              text={initialCustomer.email}
              nowrapText
              href={urls.changeEmail(initialCustomer.country)}
              ariaLabel="Edit email"
              analyticsLabel="Edit email"
            />
            <StackableButton
              text="Password"
              href={urls.changePassword(initialCustomer.country)}
              ariaLabel="Change password"
            />
            {SETTINGS_MFA_FOR_ALL.treatment === "on" && (
              <StackableButton
                labelStyle="success"
                text="Manage verification"
                nowrapText
                href={urls.manageMfa(initialCustomer.country)}
                ariaLabel="Two-step verification"
              />
            )}
          </StackableButtonGroup>
        </SettingsSection>
      )}
      <SettingsSection title="User details">
        {isStateFarm && (
          <Alert
            classNames="mb-4"
            alertType="warning"
            hasIcon
            messageText={
              <>
                For policy related assistance, please contact your State Farm
                agent at{" "}
                <Link
                  className="text-nowrap fw-bold"
                  phoneNumber={phoneNumbers.stateFarm}
                />
              </>
            }
          />
        )}
        {didUpdateSucceed && (
          <Alert
            classNames="mb-4"
            alertType="success"
            hasIcon
            headingText="Your contact information has been updated!"
          />
        )}
        <StackableButtonGroup>
          <StackableButton
            text={getDisplayName(initialCustomer)}
            nowrapText
            onClick={
              isCustomerNameEditable
                ? () => editContactInfo("cutomer name")
                : undefined
            }
            analyticsLabel="Customer name"
            ariaLabel={isCustomerNameEditable ? "Edit name" : undefined}
            footer={
              isPolicyHolder && !isStateFarm ? (
                <>
                  To edit insured name, please call{" "}
                  <Link
                    phoneNumber={phoneNumbers.auto}
                    className="text-nowrap"
                  />
                  .
                </>
              ) : undefined
            }
          />
          <StackableButton
            label="Mailing address"
            analyticsLabel="Mailing address"
            text={formatCustomerAddress(initialCustomer)}
            onClick={
              isStateFarm ? undefined : () => editContactInfo("mailing address")
            }
            ariaLabel={isStateFarm ? undefined : "Edit mailing address"}
          />
          <StackableButton
            label="Phone number"
            analyticsLabel="Phone number"
            text={
              initialCustomer.phone
                ? formatPhoneNumber(initialCustomer.phone)
                : "Not provided"
            }
            onClick={
              isStateFarm ? undefined : () => editContactInfo("phone number")
            }
            ariaLabel={isStateFarm ? undefined : "Edit phone number"}
          />
        </StackableButtonGroup>
      </SettingsSection>
      <SettingsSection title="Communication preferences">
        <StackableButtonGroup>
          {hasCorePolicy &&
            isUSPolicyHolder &&
            PAPERLESS.treatment === "on" && (
              <StackableButton
                text="Document delivery"
                href={routes.documentDelivery}
                ariaLabel="Document delivery preferences"
              />
            )}
          <StackableButton
            text="Email subscriptions"
            href={isProd ? urls.editPreferences(contactId) : undefined}
            onClick={isProd ? undefined : () => preferenceInfoDialog.open()}
            ariaLabel="Manage email subscriptions"
          />
          <StackableButton
            text="Marketplace notifications"
            href={urls.marketplaceNotifications}
            ariaLabel="Manage marketplace notifications"
          />
          <StackableButton
            text="Valuation notifications"
            href={urls.valuationNotificationPreferences}
            ariaLabel="Manage valuation notifications"
          />
        </StackableButtonGroup>
      </SettingsSection>

      {preferenceInfoDialog.isOpen && (
        <PreferencesInfoDialog
          onCloseDialog={() => preferenceInfoDialog.close()}
        />
      )}
    </SettingsContainer>
  );

  function editContactInfo(sourceType: SourceType) {
    mt.trackAction({
      actionType: "link",
      actionName: `edit contact information : ${sourceType}`,
    });

    let route = "";
    switch (sourceType) {
      case "cutomer name":
        route = routes.editName;
        break;
      case "mailing address":
        route = routes.editMailingAddress;
        break;
      case "phone number":
        route = routes.editPhoneNumber;
        break;
    }
    routes.go(route);
  }
};
